import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  contatos as api,
  type CandidatoAUnir,
  type LadoDoContato,
} from '../api/contatos'
import { ErroApi } from '../api/client'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { UniaoManual, VinculoComOAcervo } from './UniaoManual'
import { formatar } from '../i18n'
import './usuarios.css'
import './contatos.css'

/**
 * Contato único: a mesma pessoa, em canais diferentes, deixa de ser dois
 * cadastros.
 *
 * 🔴 **Unir o contato errado mistura o histórico de duas pessoas — é incidente
 * de privacidade, não erro de cadastro.** Por isso a tela mostra os dois lados
 * antes de qualquer botão, a união pede confirmação explícita, e o desfazer
 * existe e devolve tudo ao estado anterior.
 */
export function Contatos() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const [lista, setLista] = useState<CandidatoAUnir[] | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [recado, setRecado] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      setLista(await api.candidatos())
    } catch {
      setFalhou(true)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!lista) return <Estado tipo="carregando" />

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('contatos.titulo')}</h1>
          <p className="usuarios__descricao">{t('contatos.descricao')}</p>
        </div>
      </header>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {recado && <Aviso tom="sucesso">{recado}</Aviso>}

      {/* 🔴 A porta manual vem ANTES da lista de sugestões (069).
          Telegram e Instagram não dão telefone, e o id do Instagram é escopado
          por aplicativo: para esses canais a lista de candidatos é vazia por
          definição, e quem chega aqui vindo deles precisa achar a porta sem
          rolar uma lista que nunca terá nada. */}
      <UniaoManual podeUnir={pode('ParametroGerenciar')} aoConcluir={carregar} Lado={Lado} />

      <VinculoComOAcervo podeVincular={pode('ParametroGerenciar')} />

      {/* 🔴 Candidato é sugestão, nunca ação pendente. Lista vazia é boa
          notícia, não falta de trabalho. */}
      {lista.length === 0 ? (
        <Estado tipo="vazio" titulo={t('contatos.semCandidatos')} descricao={t('contatos.semCandidatosAjuda')} />
      ) : (
        lista.map((candidato) => (
          <Par
            key={`${candidato.contatoId}-${candidato.candidatoId}`}
            candidato={candidato}
            podeUnir={pode('ParametroGerenciar')}
            aoUnir={async () => {
              setErro(null)
              try {
                await api.unir(candidato.contatoId, candidato.candidatoId)
                setRecado(t('contatos.unidos'))
                await carregar()
              } catch (e) {
                const corpo = e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null
                setErro(corpo?.detail ?? t('comum.erroDescricao'))
              }
            }}
          />
        ))
      )}
    </main>
  )
}

function Par({
  candidato,
  podeUnir,
  aoUnir,
}: {
  candidato: CandidatoAUnir
  podeUnir: boolean
  aoUnir: () => Promise<void>
}) {
  const { t } = useTranslation()

  const [lados, setLados] = useState<LadoDoContato[] | null>(null)
  const [aberto, setAberto] = useState(false)
  const [unindo, setUnindo] = useState(false)

  useEffect(() => {
    if (!aberto || lados) return

    api
      .lados(candidato.contatoId, candidato.candidatoId)
      .then(setLados)
      .catch(() => setLados([]))
  }, [aberto, lados, candidato])

  return (
    <Cartao titulo={t('contatos.par', {
      um: candidato.contato ?? `#${candidato.contatoId}`,
      outro: candidato.candidato ?? `#${candidato.candidatoId}`,
    })}>
      <p className="usuarios__descricao">
        {t('contatos.provas', { provas: candidato.provas.join(' · ') })}
      </p>

      <details
        className="contatos__lados"
        open={aberto}
        onToggle={(e) => setAberto((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary>{t('contatos.verOsDoisLados')}</summary>

        {!lados ? (
          <Estado tipo="carregando" />
        ) : (
          <div className="contatos__colunas">
            {lados.map((lado) => (
              <Lado key={lado.id} lado={lado} />
            ))}
          </div>
        )}
      </details>

      {podeUnir && (
        <div className="usuarios__acoes-formulario">
          <Botao
            variante="primario"
            carregando={unindo}
            onClick={async () => {
              // 🔴 Confirmação explícita, com o efeito nomeado. Unir a pessoa
              // errada é vazamento de dado de um cliente para outro, e desfazer
              // depois não desfaz o que o assistente já respondeu.
              const segue = window.confirm(t('contatos.confirmarUniao', {
                um: candidato.contato ?? `#${candidato.contatoId}`,
                outro: candidato.candidato ?? `#${candidato.candidatoId}`,
              }))

              if (!segue) return

              setUnindo(true)
              try {
                await aoUnir()
              } finally {
                setUnindo(false)
              }
            }}
          >
            {t('contatos.unir')}
          </Botao>
        </div>
      )}
    </Cartao>
  )
}

export function Lado({ lado }: { lado: LadoDoContato }) {
  const { t } = useTranslation()

  return (
    <section className="contatos__lado">
      <h3 className="contatos__nome">{lado.nome ?? t('contatos.semNome', { id: lado.id })}</h3>

      <p className="usuarios__descricao">
        {t('contatos.desde', { quando: formatar.dataHora(lado.criadoEm) })}
      </p>

      <h4 className="contatos__subtitulo">{t('contatos.identificadores')}</h4>
      <ul className="contatos__itens">
        {lado.identificadores.map((i) => (
          <li key={`${i.tipo}-${i.valor}`}>
            <strong>{i.tipo}</strong> {i.valor}
            {i.canal && <small> · {i.canal}</small>}
          </li>
        ))}
      </ul>

      <h4 className="contatos__subtitulo">{t('contatos.porCanal')}</h4>
      {lado.porCanal.length === 0 ? (
        <p className="usuarios__descricao">{t('contatos.semConversa')}</p>
      ) : (
        <ul className="contatos__itens">
          {lado.porCanal.map((c) => (
            <li key={c.canal}>
              <strong>{c.canal}</strong>
              {' · '}
              {t('contatos.conversas', { total: c.conversas })}
              {' · '}
              {formatar.dataHora(c.ultima)}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
